import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import RiskAnalysisModal from './RiskAnalysisModal';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatHourValue } from '../utils/commonUtils';

const RiskAnalysisTable = ({ dateRangeData = [], activeMetric, isDataLoading, metricBranchDataRangeData, dateRange, date, columnName}) => {

  const defaultData = useMemo(() => {
    return dateRangeData.flatMap(sectionData =>
      sectionData.metrics?.filter(metric => activeMetric.id == metric.id).map(metric => ({
        date: sectionData.section || 'N/A',
        target: formatHourValue(metric.target?.green_level || 0, metric.unit),
        result: formatHourValue(metric.avgValue || 0, metric.unit),
        riskLevel: ""+metric.riskLevel === 'yellow' ? 'amber' : metric.riskLevel || 'red',
        metricName: metric.name,
        unit: metric.unit,
        targetLevels: metric.target
      })) || []
    );
  }, [dateRangeData, activeMetric.id, metricBranchDataRangeData]);

  const tableData = defaultData;

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState(activeMetric.name);
  const [modalTarget, setModalTarget] = useState(null);
  const [modalDate, setModalDate] = useState(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

  const hasBranchData = (row) => {
    const sample =
      metricBranchDataRangeData.filter(metric => activeMetric.id == metric.metricsId).flatMap(metric =>
        metric.branchData?.map(branch => ({
          branchName: branch.branchName,
          target: formatHourValue(branch.target, metric.metricsUnit),
          result: formatHourValue(branch.result, metric.metricsUnit),
          riskLevel: ""+branch.riskLevel === 'yellow' ? 'amber' : branch.riskLevel || 'red'
        })) || []
    ) || [];

    return sample.length > 0;
  };

  const openModal = (row) => {
    const baseTitle = activeMetric.name;
    const targetVal = row?.original?.target ?? null;

    // Use current selected date range instead of row date
    const currentDate = dateRange && dateRange.length > 0 ? dateRange[0] : null;

    if (typeof getModalData === 'function') {
      setModalTitle(baseTitle);
      setModalTarget( targetVal);
      setModalDate(currentDate);
    } else {
      console.log('ActiveMetric ID:', activeMetric.id);
      console.log('MetricBranchDataRangeData:', metricBranchDataRangeData);

      const sample =
        metricBranchDataRangeData.filter(metric => activeMetric.id == metric.metricsId).flatMap(metric =>
          metric.branchData?.map(branch => ({
            branchName: branch.branchName,
            target: formatHourValue(branch.target, metric.metricsUnit),
            result: formatHourValue(branch.result, metric.metricsUnit),
            riskLevel: ""+branch.riskLevel === 'yellow' ? 'amber' : branch.riskLevel || 'red'
          })) || []
      ) || [];

      console.log('Filtered sample data:', sample);
      setModalData(sample);
      setModalTitle(baseTitle);
      setModalTarget(targetVal);
      setModalDate(currentDate);
      if(sample.length>0){
        setShowModal(true);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
      },
      {
        accessorKey: 'target',
        header: 'Target',
      },
      {
        accessorKey: 'result',
        header: 'Result',
      },
      {
        accessorKey: 'riskLevel',
        header: 'Risk Level',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const riskLevelMap = {
            'green': 'Green',
            'amber': 'Amber',
            'red': 'Red'
          };
          return (
            <div className={`risk-level-pill ${value}`}>
              <span className="risk-level-text">{riskLevelMap[value] || value}</span>
            </div>
          );
        },
      },
    ],
    [openModal]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div key={activeMetric.id} className="data-table-container">
      <div className="table-header-section">
        <div className="table-title-header" style={{ flex: 1 }}>
          <div className="table-title-content">
            <div className="table-title-text">{activeMetric.name}</div>
          </div>
        </div>
      </div>

      <div className="table-scroll-container" style={{
        overflowX: 'auto',
        overflowY: 'hidden',
        width: '100%'
      }}>
        <div className="data-table" style={{
          width: '100%'
        }}>
          {isDataLoading ? (
            <>
              {/* Skeleton Headers */}
              {['Date', 'Target', 'Result', 'Risk Level'].map((headerText, headerIndex) => (
                <div key={headerIndex} className="table-column" style={{ flex: 1 }}>
                  <div className="table-header-cell">
                    <div className="table-header-cell-content">
                      <div className="table-header-text">{headerText}</div>
                    </div>
                  </div>
                  {/* Skeleton Rows */}
                  {[...Array(5)].map((_, rowIndex) => (
                    <div
                      key={rowIndex}
                      className={`table-row-cell ${rowIndex % 2 === 0 ? 'even' : 'odd'}`}
                    >
                      <div className="table-cell-content">
                        <div className="table-cell-text">
                          <Skeleton
                            width={headerIndex === 0 ? 120 : headerIndex === 3 ? 80 : 60}
                            height={headerIndex === 3 ? 20 : 16}
                            borderRadius={headerIndex === 3 ? 10 : 4}
                            baseColor="#f3f3f3"
                            highlightColor="#ecebeb"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : ( 
            table.getHeaderGroups().map(headerGroup =>
              headerGroup.headers.map((header, headerIndex) => (
                <div key={header.id} className="table-column" style={{ flex: 1 }}>
                  <div className="table-header-cell">
                    <div className="table-header-cell-content">
                      <div className="table-header-text">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </div>
                    </div>
                  </div>

                  {table.getRowModel().rows.map((row, rowIndex) => {
                    const cell = row.getVisibleCells()[headerIndex];
                    const isHovered = hoveredRowIndex === rowIndex;
                    const hasData = hasBranchData(row);

                    return (
                      <div
                        key={`${row.id}-${cell.column.id}`}
                        className={`table-row-cell ${rowIndex % 2 === 0 ? 'even' : 'odd'} ${hasData ? 'clickable-row' : ''} ${isHovered && hasData ? 'hovered' : ''}`}
                        onClick={hasData ? () => openModal(row) : undefined}
                        onMouseEnter={hasData ? () => setHoveredRowIndex(rowIndex) : undefined}
                        onMouseLeave={hasData ? () => setHoveredRowIndex(null) : undefined}
                      >
                        <div className="table-cell-content">
                          <div className="table-cell-text">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div> 
              ))
            )
          )}
        </div>
      </div>

      <RiskAnalysisModal
        show={showModal}
        onHide={() => setShowModal(false)}
        data={modalData}
        title={modalTitle}
        target={modalTarget}
        date={date}
        columnName={columnName}
      />
    </div> 
  );
};

export default RiskAnalysisTable;
